import { useTranslate } from '@refinedev/core';
import { Checkbox, InputNumber, Select, Space, Typography } from 'antd';
import { set as setTime } from 'date-fns';
import {
  defaultDraft,
  draftProblem,
  scheduleOfDraft,
  WEEKDAYS,
  type ReminderScheduleDraft,
  type ReminderWeekday,
} from '../../components/EsCardReminders/rrule';
import { DateFnsDatePicker, DateFnsTimePicker } from '../../components/Pickers';
import { usePickerLocale } from './usePickerLocale';

/**
 * E34 · the repeat-rule editor of a homework assignment — ONE editor for the Assign drawer and the
 * Edit dialog of a recurring row (the founder's Q-b1a-5 B: "Edit offers the schedule"). It is the E05
 * reminder editor's rule (`components/EsCardReminders/rrule`) narrowed to what a homework repeat
 * takes: daily · weekly (days) · monthly (day of the month), a time and a start. A homework repeat runs until it is
 * cancelled, unless the rule already carries an end COUNT (a tool preset's "For 14 days", or a stored bounded rule): then
 * the draft keeps `bound: 'count'` and the editor shows the count row. UNTIL is never offered. `testPrefix` keeps each host's test ids its own
 * (`homework-assign-…`, `homework-edit-…`).
 */
export type RepeatDraft = Omit<ReminderScheduleDraft, 'frequency'> & {
  frequency: 'daily' | 'weekly' | 'monthly';
};

/** A new repeat: weekly, 18:00, starting today (what the drawer opened with before block 1e). */
export const freshRepeat = (): RepeatDraft => ({
  ...defaultDraft(),
  frequency: 'weekly',
  hour: 18,
  minute: 0,
});

/**
 * A tool preset's repeat: `frequency` at hour:minute, starting `startInDays` from `now`, ending after `count`. A weekly rule runs
 * on the weekday of its start day (the editor refuses a weekly rule with no day), so its occurrences are the same as a
 * `COUNT=n` rule anchored at the start: `FREQ=WEEKLY;BYDAY=<d>;BYHOUR=18;BYMINUTE=0;COUNT=3`.
 */
export const repeatOfPreset = (
  d: { frequency: 'daily' | 'weekly'; hour: number; minute: number; startInDays: number; count: number },
  now: Date = new Date(),
): RepeatDraft => {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() + d.startInDays);
  return {
    ...defaultDraft(now),
    frequency: d.frequency,
    hour: d.hour,
    minute: d.minute,
    startDate: { year: start.getFullYear(), month: start.getMonth() + 1, day: start.getDate() },
    weekdays: d.frequency === 'weekly' ? [WEEKDAYS[(start.getDay() + 6) % 7]] : [],
    bound: 'count',
    count: d.count,
  };
};

/** The draft of a stored rule (an Edit opens on what the row has); a rule outside the three is weekly-shaped. */
export const repeatOfSchedule = (
  draft: ReminderScheduleDraft,
): RepeatDraft => ({
  ...draft,
  frequency:
    draft.frequency === 'weekly' || draft.frequency === 'monthly'
      ? draft.frequency
      : 'daily',
  bound: draft.bound === 'count' ? 'count' : 'never',
});

/** `WEEKDAY_REQUIRED` while a weekly rule has no day. */
export const repeatProblem = (r: RepeatDraft) =>
  draftProblem(r as ReminderScheduleDraft);

/** The schedule the commands take (`rrule`, `dtstart`, the zone it runs in, no exceptions). */
export const scheduleOfRepeat = (r: RepeatDraft, tzid: string) => {
  const s = scheduleOfDraft({ ...(r as ReminderScheduleDraft), bound: r.bound === 'count' ? 'count' : 'never' });
  return { rrule: s.rrule, dtstart: s.dtstart, tzid, exdates: [] as string[] };
};

/** What the count counts: the preset's unit (days of a daily repeat, weeks of a weekly one); `times` for a stored rule. */
export type RepeatCountUnit = 'days' | 'weeks' | 'times';

type Props = {
  value: RepeatDraft;
  onChange: (next: RepeatDraft) => void;
  testPrefix: string;
  /** The count row ("For [N] days"), shown only while the draft is bounded by a count. */
  count?: { max: number; unit: RepeatCountUnit };
};

export const RepeatEditor = ({ value: repeat, onChange, testPrefix, count }: Props) => {
  const t = useTranslate();
  const pickerLocale = usePickerLocale();
  const patchRepeat = (p: Partial<RepeatDraft>) => onChange({ ...repeat, ...p });

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <div>
        <Typography.Text>{t('homework.assign.repeat')}</Typography.Text>
        <Select
          style={{ width: '100%' }}
          value={repeat.frequency}
          onChange={(frequency) => patchRepeat({ frequency })}
          options={[
            { value: 'daily', label: t('homework.assign.repeatDaily') },
            { value: 'weekly', label: t('homework.assign.repeatWeekly') },
            { value: 'monthly', label: t('homework.assign.repeatMonthly') },
          ]}
          data-testid={`${testPrefix}-repeat`}
        />
      </div>
      {repeat.frequency === 'weekly' ? (
        <div>
          <Typography.Text>{t('homework.assign.onDays')}</Typography.Text>
          <Checkbox.Group
            style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
            value={repeat.weekdays}
            onChange={(v) => patchRepeat({ weekdays: v as ReminderWeekday[] })}
            options={WEEKDAYS.map((d) => ({
              value: d,
              label: t(`esReminders.day.${d.toLowerCase()}`),
            }))}
            data-testid={`${testPrefix}-days`}
          />
        </div>
      ) : null}
      {repeat.frequency === 'monthly' ? (
        <div>
          <Typography.Text>{t('homework.assign.monthDay')}</Typography.Text>
          <InputNumber
            style={{ width: '100%' }}
            min={1}
            max={31}
            value={repeat.monthDay}
            onChange={(v) => patchRepeat({ monthDay: Number(v) || 1 })}
            data-testid={`${testPrefix}-monthday`}
          />
        </div>
      ) : null}
      {repeat.bound === 'count' ? (
        <div>
          <Typography.Text>{t(`homework.assign.repeatCount.${count?.unit ?? 'times'}`)}</Typography.Text>
          <InputNumber
            style={{ width: '100%' }}
            min={1}
            max={count?.max ?? 366}
            precision={0}
            value={repeat.count}
            onChange={(v) => patchRepeat({ count: Math.max(1, Math.trunc(Number(v) || 1)) })}
            data-testid={`${testPrefix}-repeat-count`}
          />
        </div>
      ) : null}
      {/* P-1 (founder 2026-10-24): label above its field, as every other row of the drawer; the two pickers side by side with a gap */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        <div style={{ flex: '1 1 160px', minWidth: 0 }}>
          <Typography.Text style={{ display: 'block' }}>{t('homework.assign.at')}</Typography.Text>
          <DateFnsTimePicker
            style={{ width: '100%' }}
            locale={pickerLocale}
            format="HH:mm"
            minuteStep={5}
            allowClear={false}
            value={setTime(new Date(), {
              hours: repeat.hour,
              minutes: repeat.minute,
            })}
            onChange={(d: Date | Date[]) => {
              if (Array.isArray(d) || !d) return;
              patchRepeat({ hour: d.getHours(), minute: d.getMinutes() });
            }}
            data-testid={`${testPrefix}-time`}
          />
        </div>
        <div style={{ flex: '1 1 160px', minWidth: 0 }}>
          <Typography.Text style={{ display: 'block' }}>{t('homework.assign.starting')}</Typography.Text>
          <DateFnsDatePicker
            style={{ width: '100%' }}
            locale={pickerLocale}
            allowClear={false}
            format="yyyy-MM-dd"
            value={
              new Date(
                repeat.startDate.year,
                repeat.startDate.month - 1,
                repeat.startDate.day,
              )
            }
            onChange={(d: Date | null) => {
              if (!d) return;
              patchRepeat({
                startDate: {
                  year: d.getFullYear(),
                  month: d.getMonth() + 1,
                  day: d.getDate(),
                },
              });
            }}
            data-testid={`${testPrefix}-start`}
          />
        </div>
      </div>
    </Space>
  );
};
